import type { Reference } from '@medplum/fhirtypes'
import * as codeSystems from '@seen/core/codeSystems'
import { featureFlags } from '@seen/core/featureFlags'
import {
  ExtendedMedicationRequest,
  medicationLabelForGroup,
} from '@seen/core/models/medicationRequest'
import { orderBy } from 'lodash-es'
import { useMemo } from 'react'
import { useFeatureFlag } from '#src/hooks/useFeatureFlag.ts'
import { useSearch } from '#src/hooks/useSearch.ts'

export interface GroupedMedication {
  // Group representative; also supplies medicationDisplay and doseSummary.
  id?: string
  medicationCode?: string
  medicationDisplay: string
  type?: string
  startDate?: string
  lastOrderedDate?: string
  medicationRequests: ExtendedMedicationRequest[]
  latestStatus?: string
  // Elation marks a medication thread non-permanent for one-time and
  // short-course orders (a clinic dose, a 5-day antibiotic). Those are the
  // "temporary" medications, as opposed to the ones a participant stays on.
  isTemporary: boolean
  doseSummary?: string
}

export interface PartitionedMedications {
  rxMedications: GroupedMedication[]
  otcMedications: GroupedMedication[]
  activeRxMedications: GroupedMedication[]
  activeOtcMedications: GroupedMedication[]
  temporaryRxMedications: GroupedMedication[]
  temporaryOtcMedications: GroupedMedication[]
  discontinuedMedications: GroupedMedication[]
}

function isOtcMedication(medication: GroupedMedication): boolean {
  return medication.type === codeSystems.MedicationRequestType.OverTheCounter
}

export function partitionMedications(
  medications: GroupedMedication[]
): PartitionedMedications {
  // A non-permanent thread is synced as `completed` once its single dose is
  // given, so temporary medications are collected before the active filter,
  // not after it. rx/otc keep every active group, temporary ones included,
  // so the ungrouped card renders exactly what it always did.
  //
  // Stopped and cancelled groups go to Discontinued rather than Temporary:
  // those were discontinued or never dispensed, so listing them as Temporary
  // would present a historical order as current and count it in the card's
  // badge.
  const temporary = medications.filter(
    (m) =>
      m.isTemporary &&
      (m.latestStatus === 'active' || m.latestStatus === 'completed')
  )
  const currentlyActive = medications.filter((m) => m.latestStatus === 'active')
  const active = currentlyActive.filter((m) => !m.isTemporary)
  const discontinued = medications.filter(
    (m) => m.latestStatus === 'stopped' || m.latestStatus === 'cancelled'
  )

  return {
    rxMedications: currentlyActive.filter(
      (m) => m.type === codeSystems.MedicationRequestType.Prescription
    ),
    otcMedications: currentlyActive.filter(isOtcMedication),
    activeRxMedications: active.filter((m) => !isOtcMedication(m)),
    activeOtcMedications: active.filter(isOtcMedication),
    temporaryRxMedications: temporary.filter((m) => !isOtcMedication(m)),
    temporaryOtcMedications: temporary.filter(isOtcMedication),
    discontinuedMedications: discontinued,
  }
}

export function useCurrentMedications(
  patientReference: Reference | undefined
): PartitionedMedications & { isLoading: boolean } {
  const { data: medicationRequests = [], isLoading } = useSearch(
    ExtendedMedicationRequest,
    patientReference
      ? {
          patient: patientReference,
          _sort: '-_lastUpdated',
          _count: 1000,
          'status:not': ['entered-in-error', 'draft'],
        }
      : undefined
  )

  const shouldGroupByElationThread = useFeatureFlag(
    featureFlags.medicationGroupByElationThread
  )
  const partitioned = useMemo(() => {
    const medicationGroups = ExtendedMedicationRequest.groupMedications(
      medicationRequests,
      {
        groupByElationThread: shouldGroupByElationThread,
      }
    )

    const medications: GroupedMedication[] = medicationGroups.map((group) => {
      const earliest = group.requests[group.requests.length - 1]
      const representative = group.representativeRequest
      const label = medicationLabelForGroup(group)

      return {
        id: representative.id,
        medicationCode: representative.medicationCode,
        medicationDisplay: label.display,
        type: representative.type,
        startDate: earliest?.effectiveDate,
        lastOrderedDate: representative.effectiveDate,
        medicationRequests: group.requests,
        latestStatus: group.currentStatus,
        isTemporary: representative.isPermanent === false,
        doseSummary: label.doseSummary,
      }
    })

    const sortedMedications = orderBy(
      medications,
      (m) => (m.lastOrderedDate ? new Date(m.lastOrderedDate).getTime() : 0),
      ['desc']
    )

    return partitionMedications(sortedMedications)
  }, [medicationRequests, shouldGroupByElationThread])

  return {
    ...partitioned,
    isLoading,
  }
}
